import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useBuild } from '@/api/hooks';
import { useNow } from '@/lib/useNow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { BuildDetail } from '@/features/builds/detail/BuildDetail';
import { useBuildTabQueries } from '@/features/builds/detail/useBuildTabQueries';
import { useBuildsLive } from '@/features/builds/useBuildsLive';

/** Screen 03: header + stepper, tabs (timeline, checks, artifacts, env snapshot) and the metadata card; live via SSE. */
export function BuildDetailPage() {
  const { slug, buildId } = useParams({ strict: false }) as { slug: string; buildId: string };
  const build = useBuild(buildId);
  const [tab, setTab] = useState('timeline');
  const now = useNow(1_000);
  useBuildsLive(build.data?.projectId);
  const queries = useBuildTabQueries(buildId, tab);

  if (build.isLoading && !build.data) return <DetailSkeleton />;
  if (build.isError && !build.data) return <ErrorState error={build.error} onRetry={() => void build.refetch()} />;
  if (!build.data) return <BuildNotFound slug={slug} />;
  return <BuildDetail build={build.data} slug={slug} now={now} tab={tab} onTabChange={setTab} queries={queries} />;
}

function BuildNotFound({ slug }: { slug: string }) {
  return (
    <EmptyState
      icon="construction"
      title="Build not found"
      description="This build does not exist in the project or you cannot see it."
      action={
        <Button asChild variant="outline">
          <Link to="/projects/$slug/builds" params={{ slug }}>
            Back to Builds
          </Link>
        </Button>
      }
    />
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-5" aria-busy="true">
      <Skeleton className="h-4 w-28" />
      <div className="flex items-center gap-3">
        <Skeleton className="size-11 rounded-lg" />
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-20" />
      </div>
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-24 w-full rounded-lg" />
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <Skeleton className="h-80 w-full rounded-lg" />
        <Skeleton className="h-80 w-full rounded-lg" />
      </div>
    </div>
  );
}
